const EventEmitter = require('events');

import { Sample } from './sample';

const ORNAMENT_TEST_SAMPLE = new Sample({
    volumes: [
        15, 15, 15, 15, 14, 14, 14, 14, 13, 13, 13, 13, 12, 12, 12, 12, 11, 11,
        11, 11, 10, 10, 10, 10, 9, 9, 9, 9, 8, 8, 8, 8,
    ],
});

const TONE_PERIODS = [
    0x0ef8, 0x0e10, 0x0d60, 0x0c80, 0x0bd8, 0x0b28, 0x0a88, 0x09f0, 0x0960,
    0x08e0, 0x0858, 0x07e0, 0x077c, 0x0708, 0x06b0, 0x0640, 0x05ec, 0x0594,
    0x0544, 0x04f8, 0x04b0, 0x0470, 0x042c, 0x03f0, 0x03be, 0x0384, 0x0358,
    0x0320, 0x02f6, 0x02ca, 0x02a2, 0x027c, 0x0258, 0x0238, 0x0216, 0x01f8,
    0x01df, 0x01c2, 0x01ac, 0x0190, 0x017b, 0x0165, 0x0151, 0x013e, 0x012c,
    0x011c, 0x010b, 0x00fc, 0x00ef, 0x00e1, 0x00d6, 0x00c8, 0x00bd, 0x00b2,
    0x00a8, 0x009f, 0x0096, 0x008e, 0x0085, 0x007e, 0x0077, 0x0070, 0x006b,
    0x0064, 0x005e, 0x0059, 0x0054, 0x004f, 0x004b, 0x0047, 0x0042, 0x003f,
    0x003b, 0x0038, 0x0035, 0x0032, 0x002f, 0x002c, 0x002a, 0x0027, 0x0025,
    0x0023, 0x0021, 0x001f, 0x001d, 0x001c, 0x001a, 0x0019, 0x0017, 0x0016,
    0x0015, 0x0013, 0x0012, 0x0011, 0x0010, 0x000f,
].map((i) => {
    return i * 2;
});

class Chip {
    constructor(song, channelOffset) {
        this.song = song;
        this.channelOffset = channelOffset;

        this.channelStates = [];
        for (var chan = 0; chan < 3; chan++) {
            this.channelStates[chan] = {
                note: null,
                sample: null,
                ornament: null,
                sampleFrame: 0,
                envelope: 0,
                envelopePeriod: 0,
                /* last sample read from pattern data, rather than auditioned;
                this will be the active sample when playback resumes, even if a
                sample or ornament has been auditioned in the meantime */
                lastSampleRead: this.song.getSample(1),
            };
        }

        this.currentPattern = null;
        this.transpose = 0;
        this.lastEnvelope = null;
    }

    clearChannelStates() {
        for (var chan = 0; chan < 3; chan++) {
            this.channelStates[chan].note = null;
            this.channelStates[chan].sampleFrame = 0;
            this.channelStates[chan].ornament = null;
            this.channelStates[chan].envelope = 0;
            this.channelStates[chan].sample =
                this.channelStates[chan].lastSampleRead;
        }
        this.lastEnvelope = null;
        this.transpose = 0;
    }

    fetchRow(rowIndex) {
        for (var chan = 0; chan < 3; chan++) {
            var channelState = this.channelStates[chan];
            var column = this.channelOffset + chan;

            var note = this.currentPattern.getField(column, rowIndex, 'note');
            if (note === 255) {
                channelState.note = null;
            } else if (note !== 0) {
                channelState.note = note + this.transpose;
                channelState.sampleFrame = 0;
                var sampleNumber = this.currentPattern.getField(
                    column,
                    rowIndex,
                    'sample',
                );
                if (sampleNumber !== 0) {
                    channelState.sample = channelState.lastSampleRead =
                        this.song.getSample(sampleNumber);
                }
                var command = this.currentPattern.getField(
                    column,
                    rowIndex,
                    'command',
                );
                if (command === 15) {
                    /* ornament on / envelope off */
                    channelState.envelope = 0;
                    let ornamentNumber =
                        this.currentPattern.getField(
                            column,
                            rowIndex,
                            'parameter',
                        ) & 0x0f;
                    if (ornamentNumber == 0) {
                        channelState.ornament = null;
                    } else {
                        channelState.ornament =
                            this.song.getOrnament(ornamentNumber);
                    }
                } else if (command !== 0) {
                    /* ornament off / envelope on */
                    channelState.ornament = null;
                    channelState.envelope = command;
                    var param = this.currentPattern.getField(
                        column,
                        rowIndex,
                        'parameter',
                    );
                    channelState.envelopePeriod = param;
                }
            }
        }
    }

    getAudioFrame() {
        var writes = [];
        var mixer = 0xc0;
        var noiseLevel = 0;
        var envelope = 0;
        var envelopePeriod = 0;

        for (var chan = 0; chan < 3; chan++) {
            var channelState = this.channelStates[chan];
            if (
                channelState.note === null ||
                channelState.sample === null ||
                (channelState.sampleFrame >= 32 &&
                    channelState.sample.repeat === 0)
            ) {
                writes.push(chan + 8, 0);
            } else {
                var sample = channelState.sample;
                var vol;

                var sampleFrame = channelState.sampleFrame;
                if (sampleFrame >= 32) {
                    sampleFrame =
                        (((sampleFrame - 32) % sample.repeatLength) +
                            sample.repeat) %
                        32;
                }

                var note = channelState.note;
                if (channelState.ornament !== null) {
                    note += channelState.ornament.tones[sampleFrame];
                }
                var period = TONE_PERIODS[note - 1];

                period -= sample.tones[sampleFrame];
                period &= 0x0fff;
                writes.push(chan * 2, period & 0xff, chan * 2 + 1, period >> 8);

                if (channelState.envelope === 0) {
                    vol = sample.volumes[sampleFrame];
                } else {
                    vol = 16;
                    envelope = channelState.envelope;
                    envelopePeriod = channelState.envelopePeriod;
                }
                if (sample.toneMasks[sampleFrame]) {
                    mixer |= 0x01 << chan;
                }

                if (sample.noiseMasks[sampleFrame]) {
                    mixer |= 0x08 << chan;
                } else {
                    noiseLevel = sample.noiseLevels[sampleFrame];
                }

                writes.push(chan + 8, vol);
                channelState.sampleFrame++;
            }
        }

        writes.push(6, noiseLevel);
        writes.push(7, mixer);
        writes.push(11, envelopePeriod);
        if (envelope !== this.lastEnvelope) {
            writes.push(13, envelope);
            this.lastEnvelope = envelope;
        }

        return writes;
    }
}

class Player extends EventEmitter {
    constructor(ayDriver) {
        super();
        this.ayDriver = ayDriver;

        /* isActive = something in the interface has triggered audio generation -
        e.g. inserting a note, not necessarily playing continuous music data */
        this.isActive = false;
        /* isPlaying = is playing continuous music data */
        this.isPlaying = false;
    }

    openSong(song) {
        this.song = song;
        this.chips = [];
        let channelCount = song.getChannelCount();
        for (var i = 0; i < channelCount; i += 3) {
            this.chips.push(new Chip(song, i));
        }

        this.currentRow = null;
        this.currentPattern = null;
        this.currentPatternNumber = null;
        this.currentPosition = null;
        this.rowFrameNumber = 0;
        this.playSingleRow = false;
    }

    clearChannelStates() {
        this.chips.forEach((chip) => {
            chip.clearChannelStates();
        });
    }

    fetchPosition(positionNumber) {
        this.currentPosition = positionNumber;
        var position = this.song.getPosition(positionNumber);
        this.chips.forEach((chip) => {
            chip.transpose = position.transpose;
        });
        this.fetchPattern(position.pattern);
    }

    fetchPattern(patternNumber) {
        this.currentPatternNumber = patternNumber;
        this.currentPattern = this.song.getPattern(patternNumber);
        this.chips.forEach((chip) => {
            chip.currentPattern = this.currentPattern;
        });
        this.currentRow = 0;
        this.fetchRow(0);
        this.rowFrameNumber = 0;
    }

    fetchRow(rowIndex) {
        this.chips.forEach((chip) => {
            chip.fetchRow(rowIndex);
        });
    }

    getAudioFrame = () => {
        /* FIXME: account for output latency; trigger playRow / playPosition events
        when the row is actually played, not just generated */
        if (this.isPlaying && this.rowFrameNumber === 0) {
            if (this.currentRow == 0) {
                this.emit('playPattern', this.currentPatternNumber);
                if (!this.playSinglePattern) {
                    this.emit('playPosition', this.currentPosition);
                }
            }
            this.emit('playRow', this.currentPatternNumber, this.currentRow);
        }

        let writes = this.chips.map((chip) => {
            return chip.getAudioFrame();
        });

        this.rowFrameNumber++;
        if (!this.playSingleRow && this.rowFrameNumber >= this.song.tempo) {
            var nextRow = this.currentRow + 1;
            if (nextRow < this.currentPattern.length) {
                this.currentRow = nextRow;
                this.fetchRow(nextRow);
            } else if (this.playSinglePattern) {
                this.currentRow = 0;
                this.fetchRow(0);
            } else {
                // mod then increment because position numbers are 1-based
                this.fetchPosition(
                    (this.currentPosition % this.song.length) + 1,
                );
            }
            this.rowFrameNumber = 0;
        }

        return {
            registerWrites: writes,
            duration: 20_000,
        };
    };

    auditionRow(patternNumber, rowIndex) {
        if (this.isActive) return;
        this.currentPatternNumber = patternNumber;
        this.currentPattern = this.song.getPattern(patternNumber);
        this.chips.forEach((chip) => {
            chip.currentPattern = this.currentPattern;
        });
        this.playSingleRow = true;
        this.clearChannelStates();
        this.fetchRow(rowIndex);
        this.ayDriver.start(this.getAudioFrame);
        this.isActive = true;
    }

    auditionSample(sampleNumber) {
        if (this.isActive) return;
        this.playSingleRow = true;
        this.clearChannelStates();
        this.chips[0].channelStates[0].note = 49; // C-4
        this.chips[0].channelStates[0].sample =
            this.song.getSample(sampleNumber);
        this.chips[0].channelStates[0].ornament = null;
        this.chips[0].channelStates[0].sampleFrame = 0;
        this.ayDriver.start(this.getAudioFrame);
        this.isActive = true;
    }

    auditionOrnament(ornamentNumber) {
        if (this.isActive) return;
        this.playSingleRow = true;
        this.clearChannelStates();
        this.chips[0].channelStates[0].note = 49; // C-4
        this.chips[0].channelStates[0].sample = ORNAMENT_TEST_SAMPLE;
        this.chips[0].channelStates[0].ornament =
            this.song.getOrnament(ornamentNumber);
        this.chips[0].channelStates[0].sampleFrame = 0;
        this.ayDriver.start(this.getAudioFrame);
        this.isActive = true;
    }

    playPattern(patternNumber) {
        this.playSingleRow = false;
        this.playSinglePattern = true;
        this.clearChannelStates();
        this.fetchPattern(patternNumber);
        this.ayDriver.start(this.getAudioFrame);
        this.isPlaying = true;
        this.isActive = true;
        this.emit('startPlaying');
    }

    playSong() {
        this.playSingleRow = false;
        this.playSinglePattern = false;
        this.clearChannelStates();
        this.fetchPosition(1);
        this.ayDriver.start(this.getAudioFrame);
        this.isPlaying = true;
        this.isActive = true;
        this.emit('startPlaying');
    }

    stop() {
        this.ayDriver.stop();
        if (this.isPlaying) {
            this.emit('stopPlaying');
            this.isPlaying = false;
        }
        this.isActive = false;
    }
}

export { Player };
